import { baseUrl } from "../../baseUrl";
import "ag-grid-community/styles/ag-grid.css";
import { useQuery } from "@tanstack/react-query";
import { SystemUpdateAlt } from "@mui/icons-material";
import "ag-grid-community/styles/ag-theme-alpine.min.css";
import { fetchWithAuthHandling } from "../../authHandling";
import { buyerTableColumnDefs } from "./buyerTableColumns";
import { useRef, useState, useEffect, useCallback } from "react";
import type { GridApi, GridReadyEvent } from "ag-grid-community";
import { FormSubmissionTable } from "../../components/common/formSubmissionTable";
import {
  isValidPhoneNumber,
  formatPhoneNumberE164,
} from "../../utils/phoneValidator";
import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress
} from "@mui/material";

interface SortModel {
  colId: string;
  sort: "asc" | "desc";
}
interface BuyerData {
  buyers: Array<Record<string, unknown>>;
}

export function Buyers() {
  const [filters] = useState<Record<string, { filter: string }>>(
    {}
  );
  const [sortModel] = useState<SortModel[]>([]);
  const [page, _] = useState(1);
  const [pageSize, setPageSize] = useState(2000);
  const [prevData, setPrevData] = useState<BuyerData | null>(null);
  const gridApiRef = useRef<GridApi | null>(null);
  // const gridColumnApiRef = useRef<GridApi>(null);

  const { data, error, isLoading, isSuccess } = useQuery({
    queryKey: ["buyers", { filters, sortModel, page, pageSize }],
    queryFn: async () => {
      const hasFilters = Object.keys(filters).length > 0;
      const hasSorting = sortModel.length > 0;

      if (hasFilters || hasSorting) {
        const body: {
          page: number;
          limit: number;
          query: Record<string, unknown>;
        } = {
          page,
          limit: pageSize,
          query: {}
        };

        if (hasFilters) {
          const filterFields: Record<string, string> = {};
          for (const [field, filter] of Object.entries(
            filters as Record<string, { filter: string }>
          )) {
            const filterValue = filter.filter;

            if (field === "emailAddress") {
              setPageSize(1);
            }
            if (filterValue) {
              if (field === "phoneNumber") {
                try {
                  const formattedPhoneNumber = isValidPhoneNumber(
                    filterValue,
                    "CA"
                  )
                    ? formatPhoneNumberE164(filterValue, "CA")
                    : formatPhoneNumberE164(filterValue, "US")
                      ? formatPhoneNumberE164(filterValue, "US")
                      : filterValue;
                  filterFields[field] = formattedPhoneNumber;
                } catch {
                  filterFields[field] = "";
                }
              } else {
                filterFields[field] = filterValue;
              }
            }
          }
          body.query.filter = filterFields;
        }

        if (hasSorting) {
          const firstSort = sortModel[0] as {
            colId: string;
            sort: "asc" | "desc";
          };

          const sortField = firstSort.colId;
          const sortOrder = firstSort.sort === "asc" ? "ASC" : "DESC";

          body.query.sort = {
            order: sortOrder,
            field: sortField
          };
        }

        const response = await fetchWithAuthHandling(
          `${baseUrl}/buyers/search`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(body)
          }
        );

        setPageSize(500);
        return response.json();
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      });

      const response = await fetchWithAuthHandling(
        `${baseUrl}/buyers?${params.toString()}`,
        {
          method: "GET",
          credentials: "include"
        }
      );

      return response.json();
    },
    retry: false
  });

  useEffect(() => {
    if (isSuccess) {
      setPrevData(data);
    }
  }, [isSuccess, data]);

  // const onFilterChanged = useCallback(() => {
  //   const filterModel = gridApiRef.current?.getFilterModel();
  //   setFilters(filterModel || {});
  // }, []);

  // const onSortChanged = useCallback(() => {
  //   if (gridColumnApiRef.current) {
  //     const sortModel = gridColumnApiRef.current
  //       .getColumnState()
  //       .filter((col: ColumnState) => col.sort !== null)
  //       .map((col: ColumnState) => ({
  //         colId: col.colId,
  //         sort: col.sort as "asc" | "desc"
  //       }));
  //     setSortModel(sortModel);
  //   }
  // }, []);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api;
    // gridColumnApiRef.current = params.api;
  }, []);

  const rowData = error ? prevData?.buyers : data?.buyers;

  const exportToCsv = () => {
    if (gridApiRef.current) {
      gridApiRef.current.exportDataAsCsv({
        fileName: "exported-table.csv",
        processHeaderCallback: (params) => {
          const toPascalCase = (str: string) => {
            return str
              .replace(/-/g, " ")
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join("");
          };
          return toPascalCase(params.column.getColDef().headerName || "");
        }
      });
    }
  };
  return (
    <Container
      sx={{
        mt: 3,
        maxWidth: {
          xs: "100%"
        }
      }}
      disableGutters={true}
    >
      {isLoading && !prevData ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center"
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              maxWidth: {
                xs: "97%"
              },
              width: "100%"
            }}
          >
            <Typography variant="h4" gutterBottom>
              Buyers
            </Typography>
            <Button
              onClick={exportToCsv}
              type="button"
              color="primary"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              style={{
                marginBottom: "10px"
              }}
            >
              Export to CSV <SystemUpdateAlt />
            </Button>
          </Box>
          <FormSubmissionTable
            columnDefs={buyerTableColumnDefs}
            rowData={rowData || []}
            // onFilterChanged={onFilterChanged}
            // onSortChanged={onSortChanged}
            pageSize={pageSize}
            onGridReady={onGridReady}
            height="calc(100vh - 142px)"
            onCellClicked={(e) => {
              const url = `/buyers/${e.data.id}/mortgage`;
              window.open(url, "_blank");
            }}
          />
        </Box>
      )}
    </Container>
  );
}
